import { isEmpty } from 'lodash';
import { AnyObject, ModeOfTransport } from '@xbcb/shared-types';
import type {
  CreateShipmentInput,
  IdInput,
  ShipmentLegIorInput,
} from '@xbcb/api-gateway-client';

type toCreateSchemaProps = CreateShipmentInput & {
  modeOfTransport?: ModeOfTransport; // has to be optional as we are deleting this
  masterBill?: string;
  houseBills?: string[];
  ior?: ShipmentLegIorInput;
  destinationCountry?: string; // has to be optional as we are deleting this
  sourceCountry?: string; // has to be optional as we are deleting this
  usCustomsBroker?: IdInput;
  forwarder?: IdInput; // has to be optional as we are deleting this
  arrival?: AnyObject;
  workOrderGroup?: AnyObject; // has to be optional as we are deleting this
  friendlyId?: string; // has to be optional as we are deleting this
  conveyance?: AnyObject;
};
export const transformShipment = {
  toForm: ({ existingRecord }: { existingRecord: any }) => {
    return existingRecord;
  },
  toCreateSchema: ({
    input,
  }: {
    input: toCreateSchemaProps;
  }): CreateShipmentInput => {
    const {
      modeOfTransport,
      masterBill,
      houseBills,
      ior,
      destinationCountry,
      sourceCountry,
      usCustomsBroker,
      forwarder,
      legs,
      arrival,
      conveyance,
    } = input;

    if (!legs && modeOfTransport) {
      // Normalize houseBills to always be array of strings
      const normalizedHouseBills: string[] = Array.isArray(houseBills)
        ? houseBills
        : typeof houseBills === 'string'
        ? [houseBills]
        : [];

      input.legs = [
        {
          modeOfTransport,
          departure: { country: sourceCountry as string },
          arrival: {
            country: destinationCountry as string,
            customsBroker: {
              customsBroker: usCustomsBroker,
            },
            time: arrival?.time,
            portOfUnlading: arrival?.portOfUnlading,
            firmsCode: arrival?.firmsCode,
          },
          ...((masterBill || normalizedHouseBills.length > 0) && {
            masterBills: [
              {
                number: masterBill as string,
                houseBills: normalizedHouseBills.map((number) => ({ number })),
              },
            ],
          }),
          conveyance: {
            tripNumber: conveyance?.tripNumber,
            grossWeight: conveyance?.grossWeight,
          },
          ior,
        },
      ];

      delete input.modeOfTransport;
      delete input.arrival;
      delete input.sourceCountry;
      delete input.destinationCountry;
      delete input.masterBill;
      delete input.houseBills;
      delete input.ior;
      delete input.usCustomsBroker;
      delete input.conveyance;
      delete input.workOrderGroup;
      delete input.friendlyId;
      delete input.operator;
    }

    if (!isEmpty(forwarder)) {
      input.forwarders = [
        {
          forwarder,
        },
      ];
    }
    delete input.forwarder;

    // If setAsBookingId checkbox was checked, copy clientIdentifier to clientReferences.bookingId
    if ((input as any).setAsBookingId && input.clientIdentifier) {
      input.clientReferences = {
        ...input.clientReferences,
        bookingId: input.clientIdentifier,
      };
    }
    delete (input as any).setAsBookingId;

    return input;
  },
  toUpdateSchema: ({ input }: { input: any }) => {
    const { legs } = input;
    legs?.forEach((leg: AnyObject) => {
      const shipmentLegIor = leg.ior?.ior;
      if (shipmentLegIor) {
        // leg.ior.ior only takes IdInput, don't include the other values like
        // name and version
        leg.ior.ior = { id: shipmentLegIor.id };
      }
    });
    delete input.requestedWorkOrders;
    delete input.friendlyId;
    delete input.operator;
    delete input.workOrderGroup;
    delete input.status;
    return input;
  },
};
